import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";

import { AuthProvider } from "./context/AuthProvider";
import { NotificationProvider } from "./components/common/NotificationProvider"; 
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import PageLayout from "./components/layout/PageLayout";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { ErrorBoundary } from './components/ErrorBoundary';
import { PendingUpiWatcher } from "./components/wallet/PendingUpiWatcher";
import { AndroidShell } from "./native/AndroidShell";

// --- PAGES ---
import Home from "./pages/Home";               
import HomeRegular from "./pages/HomeRegular"; 
import KitchenMenu from "./pages/KitchenMenu";               
import KitchenMenuRegular from "./pages/KitchenMenuRegular"; 
import SubscriptionPage from "./pages/Subscription";         
import SubscriptionRegular from "./pages/SubscriptionRegular"; 
import Login from "./pages/Login";
import Signup from "./pages/SignUp"; 
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderHistory from "./pages/OrderHistory"; 
import Profile from "./pages/Profile";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import { Subscription } from "./pages/Subscription";

// Pages - Role Based
import ChefDashboard from "./pages/chef/ChefDashboard";
import KitchenManagerDashboard from "./pages/kitchen/KitchenManagerDashboard";
import AdminDashboard from "./pages/admin/AdminDashboard"; 
import CommunityOnboarding from "./pages/admin/CommunityOnboarding";
import DeliveryDashboard from "./pages/delivery/DeliveryDashboard";
import UserDashboard from "./pages/user/UserDashboard";
import CashierDashboard from "./pages/cashier/CashierDashboard";
import OrderDetails from "./pages/orders/OrderDetails";
import { WhatsAppDashboard } from './components/admin/whatsapp/WhatsAppDashboard';
import MyTiffin from "./pages/MyTiffin";
import MyDailyTiffin from "./pages/MyDailyTiffin";
import SubscriptionPaymentPage from "./pages/SubscriptionPaymentPage";
import AnnouncePage from "./pages/chef/AnnouncePage";
import ExploreMenu from "./pages/ExploreMenu";
import DailyMenu from "./pages/DailyMenu";

const USE_SCIFI_LAYOUT = false;

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}>
          <AuthProvider>
            <AndroidShell />
            <NotificationProvider />
            
            <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col">
              <Navbar />
              <Toaster position="top-center" />
              <PendingUpiWatcher />
              
              <div className={USE_SCIFI_LAYOUT ? "flex-grow" : ""}>
                <ErrorBoundary fallback={<div className="p-10 text-center text-red-600">Route Error - Please go back</div>}>
                  <Routes>
                    {/* === HOME/KITCHEN - DUAL LAYOUT === */}
                    <Route path="/" element={USE_SCIFI_LAYOUT ? <Home /> : <HomeRegular />} />
                    <Route path="/kitchen/:kitchenId" element={USE_SCIFI_LAYOUT ? <KitchenMenu /> : <KitchenMenuRegular />} />
                    <Route path="/subscription" element={USE_SCIFI_LAYOUT ? <SubscriptionPage /> : <SubscriptionRegular />} />
                    <Route path="/menu" element={<ExploreMenu />} />
                    <Route path="/daily-menu" element={<DailyMenu />} />

                    {/* === SCI-FI ROUTES === */}
                    <Route path="/scifi" element={<Home />} />
                    <Route path="/scifi/kitchen/:kitchenId" element={<KitchenMenu />} />
                    <Route path="/scifi/subscription" element={<SubscriptionPage />} />

                    {/* === SHARED ROUTES === */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/cart" element={<Cart />} />
                    
                    <Route path="/checkout" element={
                      <ProtectedRoute>
                        <Checkout />
                      </ProtectedRoute>
                    } />
                    <Route path="/orders" element={
                      <ProtectedRoute>
                        <OrderHistory />
                      </ProtectedRoute>
                    } />
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    } />

                    {/* === ROLE-BASED DASHBOARDS === */}
                    {/* Customer Dashboard */}
                    <Route path="/customer/dashboard" element={
                      <ProtectedRoute allowedRoles={['customer']}>
                        <UserDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Chef Dashboard */}
                    <Route path="/chef/dashboard" element={
                      <ProtectedRoute allowedRoles={['chef', 'admin']}>
                        <ChefDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Paste / forward announcement editor (linked from the WhatsApp "Edit" reply) */}
                    <Route path="/announce" element={
                      <ProtectedRoute allowedRoles={['chef', 'kitchenOwner', 'admin']}>
                        <AnnouncePage />
                      </ProtectedRoute>
                    } />

                    {/* Kitchen Manager Dashboard */}
                    <Route path="/kitchen/dashboard" element={
                      <ProtectedRoute allowedRoles={['kitchenOwner', 'admin']}>
                        <KitchenManagerDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/kitchenmanager/dashboard" element={
                      <ProtectedRoute allowedRoles={['kitchenOwner', 'admin']}>
                        <KitchenManagerDashboard />
                      </ProtectedRoute>
                    } />
                    
                    {/* Admin Dashboard */}
                    <Route path="/admin/dashboard" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="/admin/community/onboard" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <PageLayout>
                          <CommunityOnboarding />
                        </PageLayout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Delivery Agent Dashboard */}
                    <Route path="/deliveryagent/dashboard" element={
                      <ProtectedRoute allowedRoles={['deliveryAgent', 'admin']}>
                        <DeliveryDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Cashier Dashboard */}
                    <Route path="/cashier/dashboard" element={
                      <ProtectedRoute allowedRoles={['cashier', 'admin']}>
                        <CashierDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Delivery (backward compatibility) */}
                    <Route path="/delivery/DeliveryDashboard" element={
                      <ProtectedRoute allowedRoles={['deliveryAgent', 'admin']}>
                        <DeliveryDashboard />
                      </ProtectedRoute>
                    } />
{/* Other Routes */}
                    <Route path="/orders/:orderId" element={<OrderDetails />} />
                    <Route path="/admin/whatsapp" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <WhatsAppDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/my-tiffin" element={
                      <ProtectedRoute>
                        <MyTiffin />
                      </ProtectedRoute>
                    } />
                    <Route path="/today-tiffin" element={
                      <ProtectedRoute>
                        <MyDailyTiffin />
                      </ProtectedRoute>
                    } />
                    <Route path="/subscription/payment" element={
                      <ProtectedRoute>
                        <SubscriptionPaymentPage />
                      </ProtectedRoute>
                    } />

                    {/* 404 - Catch all */}
                    <Route path="*" element={<div className="p-10 text-center">404 - Page Not Found</div>} />
                  </Routes>
                </ErrorBoundary>
              </div>

              {USE_SCIFI_LAYOUT && <Footer />}
            </div>
          </AuthProvider>
        </Router>
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
